import type React from "react";
import { useState } from "react";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import {
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  styled,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import { Lock as LockIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import type { User, UserOrderColumn, UserPagination, UserPaginationParams } from "src/types";
import { StickyTable } from "./StickyTable";
import { useUserDeleteMutation } from "src/hooks/users";
import { useConfirm } from "material-ui-confirm";
import TablePaginationActions from "@mui/material/TablePagination/TablePaginationActions";
import { TableHeadCell } from "src/components/TableHeadCell";

interface Props {
  className?: string;
  usersPaginated: UserPagination;
  userPaginationParams: UserPaginationParams;
  setUserPaginationParams: (userPaginationParams: UserPaginationParams) => void;
  showLoader: boolean;
}

export function UsersTable(props: Props) {
  const { usersPaginated, userPaginationParams, setUserPaginationParams, showLoader } = props;
  const { t } = useTranslation();

  function handleSort(column: UserOrderColumn) {
    if (column !== userPaginationParams.orderBy) {
      setUserPaginationParams({ ...userPaginationParams, orderBy: column, orderDirection: "asc" });
    }
    if (column === userPaginationParams.orderBy) {
      setUserPaginationParams({
        ...userPaginationParams,
        orderDirection: userPaginationParams.orderDirection === "asc" ? "desc" : "asc",
      });
    }
  }

  return render();

  function render() {
    return (
      <>
        <StickyTable className={props.className}>
          <TableHead>
            <TableRow>
              <TableHeadCell
                dataKey="firstName"
                orderBy={userPaginationParams.orderBy}
                orderDirection={userPaginationParams.orderDirection}
                onSort={() => handleSort("firstName")}
                tooltip={t("label.firstName")}
              >
                {t("label.firstName")}
              </TableHeadCell>
              <TableHeadCell
                dataKey="lastName"
                orderBy={userPaginationParams.orderBy}
                orderDirection={userPaginationParams.orderDirection}
                onSort={() => handleSort("lastName")}
                tooltip={t("label.lastName")}
              >
                {t("label.lastName")}
              </TableHeadCell>
              <TableCell>{t("label.email")}</TableCell>
              <TableHeadCell
                dataKey="dateCreated"
                orderBy={userPaginationParams.orderBy}
                orderDirection={userPaginationParams.orderDirection}
                onSort={() => handleSort("dateCreated")}
                tooltip={t("label.created")}
              >
                {t("label.created")}
              </TableHeadCell>
              <TableCell>{t("label.role")}</TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {showLoader &&
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={8} align="center">
                    <Skeleton variant="text" height={25} />
                  </TableCell>
                </TableRow>
              ))}

            {!showLoader &&
              usersPaginated.data.map((item) => (
                <TableRow key={item.id}>
                  <TableCell component="th" scope="row">
                    {item.firstName}
                  </TableCell>
                  <TableCell>{item.lastName}</TableCell>
                  <TableCell>{item.email}</TableCell>
                  <TableCell>{t("label.dateCreated", { date: item.dateCreated })}</TableCell>
                  <TableCell>{t(`label.userRoles.${item.role}`)}</TableCell>
                  <TableCell>
                    {!item.enabled && (
                      <Tooltip title={t<string>("label.thisUserIsDisabled")}>
                        <LockIcon fontSize="small" color="action" />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <RowActions data={item} />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </StickyTable>
        <StyledTablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50, { label: "All", value: usersPaginated.totalCount }]}
          count={usersPaginated.totalCount}
          rowsPerPage={usersPaginated.limit}
          page={usersPaginated.offset}
          labelRowsPerPage={t("label.rowsPerPage")}
          labelDisplayedRows={({ from, to, count }) => t("label.displayedRows", { from, to, count })}
          onPageChange={(event, page) => setUserPaginationParams({ ...userPaginationParams, offset: page })}
          onRowsPerPageChange={(event) =>
            setUserPaginationParams({
              ...userPaginationParams,
              limit: Number.parseInt(event.target.value),
              offset: 0,
            })
          }
          ActionsComponent={TablePaginationActions}
        />
      </>
    );
  }
}

function RowActions(props: { data: User }) {
  const { data } = props;
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // // TODO: refactor into hook
  // const setDefaultMutation = useMutation(CustomMeteoService.setDefault, {
  //   onSuccess: () => queryClient.invalidateQueries('custommeteo'),
  // });

  // // TODO: refactor into hook
  const deleteMutation = useUserDeleteMutation();

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  // function handleSetAsDefault() {
  //   setDefaultMutation.mutate(data.id);
  //   handleClose();
  // }

  function handleDelete() {
    confirm({
      confirmationButtonProps: { autoFocus: true },
      title: t("label.areYouSure"),
      description: t("confirm.deleteUser", {
        name: [data.firstName, data.lastName].join(" "),
      }),
    })
      .then(() => deleteMutation.mutate(data.id))
      .finally(() => handleClose());
  }

  return render();

  function render() {
    return (
      <>
        <IconButton onClick={handleClick} size="large">
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          <MenuItem
            // onClick={handleEdit}
            component={NavLink}
            to={`/users/${data.id}`}
          >
            {t("label.edit")}
          </MenuItem>
          <MenuItem onClick={handleDelete}>{t("label.delete")}</MenuItem>
        </Menu>
      </>
    );
  }
}

const StyledTablePagination = styled(TablePagination)`
  display: flex;
  justify-content: flex-end;
  background-color: ${({ theme }) => theme.palette.background.paper};
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
  p {
    color: ${({ theme }) => theme.palette.text.primary};
  }
` as typeof TablePagination;
